import { CommonModule } from "@angular/common";
import moment from "moment-timezone";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import * as CanvasJS from "./canvasjs.stock.min";
import { BackendApiService } from "../../../app/services/backend-api.service";
import { BaseUrlConstantsService } from "../../../app/services/base-url-constants.service";
import { SpinnerComponent } from "../../spinner/spinner.component";

@Component({
  selector: "app-oven-chart-modal",
  styleUrls: ["./oven-chart-modal.component.scss"],
  templateUrl: "./oven-chart-modal.component.html",
  standalone: true,
  imports: [MatIcon, CommonModule, MatButtonModule, SpinnerComponent],
})
export class OvenChartModalComponent implements OnInit, AfterViewInit {
  @Input("subsystem_id") subsystem_id: string;
  @Input("client_id") client_id: string;
  @Input("parameter") parameter: string;
  @Output() close_modal = new EventEmitter<null>();
  public baseOvenUrl = BaseUrlConstantsService.OVEN_BASE_URL;

  selectedFilter = "day";
  chart: any; // CanvasJS chart instance
  chart_Loading = true;
  no_data = false;

  chartUnit: string = "";
  stripLines: any[] = [];
  chart_type = "";
  day_1 = 0;
  days_7 = 0;
  days_30 = 0;

  constructor(private srv: BackendApiService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Don't fetch data immediately, wait for view to initialize
  }

  ngAfterViewInit(): void {
    // Add a small delay to ensure the DOM is fully rendered
    setTimeout(() => {
      this.fetchDataV2();
    }, 100);
  }

  closeModal() {
    this.close_modal.emit();
  }

  setFilter(filter: string) {
    this.selectedFilter = filter;
    this.fetchDataV2();
    this.chart = null;
    document.getElementById("chartContainer").innerHTML = "";
  }

  private renderChart(response: any, datasets: any[]) {
    // Ensure the container exists and has dimensions
    const container = document.getElementById("chartContainer");
    if (!container) {
      console.error("Chart container not found");
      return;
    }
    let self = this;
    // Create chart configuration
    const chartConfig = {
      theme: "light2",
      animationEnabled: true,
      exportEnabled: false,
      zoomEnabled: true,
      title: {
        text: this.computeChartname(
          datasets.length,
          datasets[0].name,
          this.chartUnit
        ),
        fontFamily: "Roboto-Regular",
        fontColor: "#444",
        fontSize: 26,
      },
      rangeSelector: { enabled: false },
      navigator: { enabled: true },
      charts: [
        {
          data: datasets,
          axisY:
            this.chart_type === "column"
              ? {
                  minimum: 0,
                  maximum: 1,
                  labelFormatter: () => "", // ✅ hide numbers (0, 0.2, … 1)
                  tickLength: 0, // ✅ hide tick marks
                  lineThickness: 0, // ✅ hide axis line if you want
                  gridThickness: 0, // ✅ hide background grid lines
                }
              : {
                  minimum: 0,
                    maximum: 210,
                  stripLines: this.chartUnit ? this.stripLines : [],
                  title: this.computeChartname(
                    datasets.length,
                    datasets[0].name,
                    null
                  ),
                },
          // axisY:
          //   this.chart_type == "column"
          //     ? {
          //         // ✅ hide Y-axis
          //         lineThickness: 0,
          //         tickLength: 0,
          //         labelFormatter: () => "",
          //         gridThickness: 0,
          //       }
          //     : {
          //         minimum: 0,
          //         stripLines: this.chartUnit ? this.stripLines : [],
          //         title: this.chartUnit,
          //       },

          subtitles: [
            {
              text: datasets.length === 0 ? "No data available." : "",
              verticalAlign: "center",
              horizontalAlign: "center",
              fontWeight: "bold",
            },
          ],
          toolTip: {
            shared: true, // so you see multiple series at the same x
            contentFormatter: function (e) {
              const date = e.entries[0].dataPoint.x;
              const formattedDate = date.toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              });
              let content = `<strong>${formattedDate}</strong><br/>`;
              e.entries.forEach((entry) => {
                content += `${entry.dataSeries.name}: <strong>${
                  self.chart_type == "line"
                    ? entry.dataPoint.y
                    : entry.dataPoint.org_y == 0
                    ? "OFF"
                    : "ON"
                }</strong><br/>`;
              });
              return content;
            },
          },
        },
      ],
    };

    if (this.chart) {
      this.chart.destroy();
    }

    // Create new chart
    this.chart = new CanvasJS.StockChart("chartContainer", chartConfig);

    // Render chart
    this.chart.render();

    // Force a resize after a short delay to ensure proper sizing
    setTimeout(() => {
      if (this.chart) {
        this.chart.render();
      }
    }, 50);
  }

  public fetchDataV2() {
    this.chart_Loading = true;
    let timezone = localStorage.getItem('clientTimeZone') || 'Asia/Kolkata';
    this.srv
      .getDataV2(
        `${this.baseOvenUrl}api/v1/oven-assets/params-chart/${this.subsystem_id}/${this.client_id}/${this.parameter}/${this.selectedFilter}`
        // `http://localhost:3024/api/v1/oven-assets/params-chart/${this.subsystem_id}/${this.client_id}/${this.parameter}/${this.selectedFilter}`
      )
      .then((sRes) => {
        let sensorRes = JSON.parse(JSON.stringify(sRes)).data.data;
        if (sensorRes.body && Object.keys(sensorRes.body).length !== 0) {
          let response = sensorRes.body;

          // Moving averages
          if (response.movingAvg) {
            this.day_1 = response.movingAvg[0].x_hour?.toFixed(2) || 0;
            this.days_7 = response.movingAvg[0].x_week?.toFixed(2) || 0;
            this.days_30 = response.movingAvg[0].x_month?.toFixed(2) || 0;
          }

          // Unit
          this.chartUnit = response.symbol || "";

          // Strip lines (thresholds)
          if (response.rangeLabels) {
            const range = response.rangeLabels;
            // console.log("range: ",range);
            this.stripLines = [
              {
                startValue: 0,
                endValue: range.under_red,
                color: "rgba(255, 230, 230, 0.5)", // RED
              },
              {
                value: range.under_red,
                label: range.under_red.toString(),
                labelFontColor: "#000",
                labelAlign: "right",
              },
              {
                startValue: range.under_red,
                endValue: range.under_yellow,
                color: "rgba(255, 250, 196, 0.6)", // Yellow
              },
              {
                value: range.under_yellow,
                label: range.under_yellow.toString(),
                labelFontColor: "#000",
                labelAlign: "right",
              },
              {
                startValue: range.under_yellow,
                endValue: range.under_green,
                color: "rgba(212, 237, 218, 0.5)", // Green
              },
              {
                value: range.under_green,
                label: range.under_green.toString(),
                labelFontColor: "#000",
                labelAlign: "right",
              },
              {
                startValue: range.under_green,
                endValue: range.over_yellow,
                color: "rgba(255, 250, 196, 0.6)", // Yellow
              },
              {
                value: range.over_yellow,
                label: range.over_yellow.toString(),
                labelFontColor: "#000",
                labelAlign: "right",
              },
              {
                startValue: range.over_yellow,
                endValue: 10000,
                color: "rgba(255, 230, 230, 0.5)", // RED
              },
              {
                value: range.max_value,
                label: range.max_value.toString(),
                labelFontColor: "#000",
                labelAlign: "right",
              },
            ];
          }
          // Prepare dataset
          let datasets: any[] = [];
          if (response.sensor && response.sensor.length > 0) {
            datasets = response.sensor.map((series) => {
              this.chart_type = series.type;
              return {
                type: series.type,
                showInLegend: true,
                name: series.name,
                dataPoints: series.dataPoints.map((point) => {
                  return ({
                  x: moment.tz(point.label, timezone).toDate(),
                  y:
                    series.type == "line"
                      ? point.value ?? parseFloat(point.y)
                      : 1,
                  org_y:
                    series.type == "column"
                      ? point.value ?? point.y
                      : undefined,
                  color:
                    this.chart_type == "column"
                      ? parseFloat(point.y) % 2 == 0
                        ? "#f44336"
                        : "#4caf50"
                      : undefined, //0-off-red ::: 1-on-green
                })
                }),
              };
            });
          }

          // Update loading state first
          this.chart_Loading = false;
          this.cdr.detectChanges();

          // Render chart after DOM updates
          if (datasets[0].dataPoints.length > 2) {
            setTimeout(() => {
              this.renderChart(response, datasets);
            }, 1000);
            this.no_data = false;
          } else {
            this.chart_Loading = false;
            this.no_data = true;
          }
        } else {
          this.chart_Loading = false;
          this.no_data = true;
        }
      })
      .catch((err) => {
        console.error(err);
        this.chart_Loading = false;
        this.no_data = true;
      });
  }

  computeChartname(dataLen, name, unit) {
    if (!dataLen) return "Oven Parameters";
    if (!unit) return name;
    return name + " (" + unit + ")";
  }
}
